import { useState, type FormEvent } from "react";
import { flagReview, type FlagPayload } from "@/services/reviewService";
import { ApiError } from "@/services/http";

interface ReportReviewProps {
  reviewId:    number | string;
  onCancel:    () => void;
  onSubmitted: () => void;
}

const REASONS: { value: string; label: string }[] = [
  { value: "spam",       label: "Spam or advertising" },
  { value: "offensive",  label: "Offensive language" },
  { value: "fake",       label: "Fake or misleading" },
  { value: "irrelevant", label: "Not relevant to this business" },
  { value: "other",      label: "Something else" },
];

const selectClass =
  "h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

export default function ReportReview({ reviewId, onCancel, onSubmitted }: ReportReviewProps) {
  const [reason,     setReason]     = useState("spam");
  const [details,    setDetails]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload: FlagPayload = { reason, details: details.trim() || undefined };
    try {
      await flagReview(reviewId, payload);
      onSubmitted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send your report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-4">
      <p className="font-heading text-sm font-bold text-foreground-950">Report this review</p>
      <p className="mt-0.5 text-xs text-foreground-500">
        Our moderation team will review it. Reports are confidential.
      </p>

      <div className="mt-3">
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600" htmlFor={`rf-reason-${reviewId}`}>
          Why are you reporting this?
        </label>
        <select
          id={`rf-reason-${reviewId}`}
          name="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={selectClass}
        >
          {REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-3">
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600" htmlFor={`rf-details-${reviewId}`}>
          Additional details (optional)
        </label>
        <textarea
          id={`rf-details-${reviewId}`}
          name="details"
          rows={2}
          maxLength={500}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Anything else that helps our team review this report…"
          className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-rose-600">
          <i className="ri-error-warning-line text-sm" />{error}
        </p>
      )}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-rose-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? <><i className="ri-loader-4-line animate-spin text-sm" /> Sending…</>
            : <><i className="ri-flag-2-line text-sm" /> Report review</>}
        </button>
      </div>
    </form>
  );
}